import { getToken } from "../../../utils/session";
import { toWebSocketUrl } from "../../../utils/socket/webSocketUrl";

// Global per-user socket: /ws/user/{username}?token=... (raw FastAPI WebSocket,
// same base URL as the chat socket). The server pushes a batch_update frame on
// connect and whenever the user's unread/notification counts or online friends
// change, plus a keepalive on idle. We reply to nothing except a periodic ping.

let socket = null;
let activeUsername = "";
let pingTimer = null;
let reconnectTimer = null;
let reconnectAttempt = 0;
let connectionOptions = null;

const getWsBaseUrl = () => process.env.REACT_APP_WS_URL || "";

const buildUserWsUrl = ({ username, token }) => {
  const base = toWebSocketUrl(getWsBaseUrl());
  if (!base || !username || !token) return "";
  const params = new URLSearchParams({ token });
  return `${base}/ws/user/${encodeURIComponent(username)}?${params.toString()}`;
};

const clearPing = () => {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
};

const clearReconnect = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const openUserSocket = () => {
  const options = connectionOptions;
  if (!options) return null;
  const { username } = options;
  const token = getToken();
  const url = buildUserWsUrl({ username, token });
  if (!url) return null;

  const nextSocket = new WebSocket(url);
  socket = nextSocket;

  nextSocket.addEventListener("open", () => {
    if (socket !== nextSocket) return;
    clearReconnect();
    reconnectAttempt = 0;
    clearPing();
    // Ping every 30s to keep the connection alive through idle proxies; the
    // server answers "pong" and the receive loop stays open.
    pingTimer = setInterval(() => {
      if (nextSocket.readyState === WebSocket.OPEN) {
        nextSocket.send("ping");
      }
    }, 30000);
    connectionOptions?.onOpen?.();
  });

  nextSocket.addEventListener("message", (event) => {
    if (socket !== nextSocket) return;
    if (event.data === "pong") return;
    try {
      connectionOptions?.onMessage?.(JSON.parse(event.data));
    } catch {
      // non-JSON text (e.g. an unexpected keepalive) — ignore
    }
  });

  nextSocket.addEventListener("close", (event) => {
    if (socket !== nextSocket) return;
    socket = null;
    clearPing();
    // Reconnecting also requests a fresh authoritative batch_update from the
    // server. Stop on an authentication rejection instead of retrying forever.
    if (connectionOptions && event.code !== 1008) {
      const delay = Math.min(1000 * 2 ** reconnectAttempt++, 30000);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        openUserSocket();
      }, delay);
    }
    connectionOptions?.onClose?.(event);
  });

  nextSocket.addEventListener("error", () => {
    // Browser WebSocket errors hide HTTP detail; the close handler cleans up.
  });

  return nextSocket;
};

export const connectUserSocket = (options) => {
  const { username } = options;
  if (!buildUserWsUrl({ username, token: getToken() })) return null;
  if (
    activeUsername === username &&
    (socket?.readyState === WebSocket.OPEN ||
      socket?.readyState === WebSocket.CONNECTING ||
      reconnectTimer)
  ) {
    connectionOptions = options;
    return socket;
  }
  closeUserSocket();
  activeUsername = username;
  connectionOptions = options;
  return openUserSocket();
};

export const closeUserSocket = () => {
  connectionOptions = null;
  clearReconnect();
  clearPing();
  const previousSocket = socket;
  socket = null;
  activeUsername = "";
  reconnectAttempt = 0;
  previousSocket?.close();
};

const UserSocketService = {
  connect: connectUserSocket,
  close: closeUserSocket,
};

export default UserSocketService;
